import { alpha } from '@mui/material/styles';
import { FONT_SIZES } from '../../Theme';

export const NEGOTIATION_MODAL_MAX_WIDTH = 620;
export const NEGOTIATION_MODAL_MAX_HEIGHT = 900;
export const CONFIRM_DIALOG_MAX_WIDTH = 480;
export const MODAL_PX = { xs: 2, sm: 3 };
export const SECTION_GAP = 2.5;
export const INSET_GAP = 1.5;
export const FOOTER_PY = 2;
export const HERO_PY = 2.5;

export const getDialogPaperSx = (theme) => ({
  borderRadius: { xs: 0, sm: '16px' },
  margin: { xs: 0, sm: 2 },
  width: { xs: '100%', sm: `min(${NEGOTIATION_MODAL_MAX_WIDTH}px, calc(100vw - 32px))` },
  maxWidth: { xs: '100%', sm: NEGOTIATION_MODAL_MAX_WIDTH },
  maxHeight: { xs: '100dvh', sm: `min(${NEGOTIATION_MODAL_MAX_HEIGHT}px, calc(100dvh - 32px))` },
  height: { xs: '100dvh', sm: 'auto' },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: theme.palette.chat?.windowShadow || theme.shadows[8],
});

export const headerSx = {
  px: MODAL_PX,
  pt: { xs: 2, sm: 2.5 },
  pb: 1.25,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 2,
};

export const titleSx = {
  fontSize: FONT_SIZES.title,
  fontWeight: 650,
  letterSpacing: '-0.02em',
  color: 'text.primary',
  lineHeight: 1.25,
};

export const subtitleSx = {
  mt: 0.5,
  fontSize: FONT_SIZES.bodySm,
  color: 'text.secondary',
  lineHeight: 1.45,
};

export const closeButtonSx = {
  width: 36,
  height: 36,
  color: 'text.secondary',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  '&:focus-visible': {
    outline: (theme) => `2px solid ${theme.palette.functional?.main || theme.palette.primary.main}`,
    outlineOffset: 2,
  },
};

export const contentSx = {
  px: MODAL_PX,
  pt: 1,
  pb: 3,
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: SECTION_GAP,
};

export const inlineErrorSx = (theme) => ({
  borderRadius: '12px',
  bgcolor: alpha(theme.palette.error.main, 0.08),
  color: 'error.main',
  border: `1px solid ${alpha(theme.palette.error.main, 0.16)}`,
  py: 1,
  alignItems: 'flex-start',
  '& .MuiAlert-icon': {
    color: 'error.main',
  },
});

export const offerHeroSx = (theme) => ({
  p: { xs: 2, sm: HERO_PY },
  borderRadius: '14px',
  border: `1px solid ${theme.palette.border?.subtle || theme.palette.divider}`,
  bgcolor: theme.palette.decorative?.surfaceWash || alpha(theme.palette.text.primary, 0.025),
});

export const overlineSx = {
  fontSize: FONT_SIZES.micro,
  fontWeight: 650,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
};

export const amountSx = {
  fontSize: FONT_SIZES.display,
  fontWeight: 650,
  letterSpacing: '-0.04em',
  lineHeight: 1,
  color: 'text.primary',
  fontVariantNumeric: 'tabular-nums',
};

export const amountLabelSx = {
  mt: 0.5,
  fontSize: FONT_SIZES.bodySm,
  fontWeight: 500,
  color: 'text.secondary',
};

export const mutedLineSx = {
  fontSize: FONT_SIZES.caption,
  color: 'text.muted',
  lineHeight: 1.45,
};

export const selectorTrackSx = (theme) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 0.5,
  p: 0.375,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.border?.subtle || theme.palette.divider}`,
  bgcolor: 'background.paper',
});

export const getSegmentSx = (theme, selected, disabled) => ({
  minHeight: 44,
  px: { xs: 1, sm: 1.5 },
  borderRadius: '9px',
  border: '1px solid transparent',
  color: selected ? 'text.primary' : 'text.secondary',
  bgcolor: selected ? (theme.palette.decorative?.surfaceWash || alpha(theme.palette.text.primary, 0.035)) : 'transparent',
  boxShadow: selected ? theme.palette.chat?.selectedRowShadow : 'none',
  fontSize: FONT_SIZES.bodySm,
  fontWeight: selected ? 650 : 500,
  textTransform: 'none',
  opacity: disabled ? 0.42 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'background-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease',
  '&:hover': {
    bgcolor: (() => {
      if (disabled) return 'transparent';
      if (selected) return theme.palette.decorative?.surfaceWash || alpha(theme.palette.text.primary, 0.035);
      return 'action.hover';
    })(),
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.functional?.main || theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

export const statusBarSx = (theme, tone = 'neutral') => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.25,
  px: 1.5,
  py: 1,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.border?.subtle || theme.palette.divider}`,
  bgcolor: (() => {
    if (tone === 'danger') return alpha(theme.palette.error.main, 0.06);
    if (tone === 'warning') return alpha(theme.palette.warning.main, 0.08);
    return 'background.paper';
  })(),
});

export const panelSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: INSET_GAP,
};

export const sectionTitleSx = {
  fontSize: FONT_SIZES.body,
  fontWeight: 650,
  color: 'text.primary',
  lineHeight: 1.35,
};

export const rateInputSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: 'background.paper',
    fontSize: FONT_SIZES.title,
    fontWeight: 550,
    fontVariantNumeric: 'tabular-nums',
    '& fieldset': {
      borderColor: theme.palette.border?.subtle || theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.functional?.main || theme.palette.primary.main,
      borderWidth: 1,
    },
  },
  '& .MuiInputAdornment-root': {
    color: 'text.secondary',
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
    fontSize: FONT_SIZES.caption,
  },
});

export const pillSx = (theme, selected = false) => ({
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: 34,
  px: 1.5,
  py: 0.75,
  borderRadius: '999px',
  border: `1px solid ${selected ? theme.palette.text.primary : (theme.palette.border?.subtle || theme.palette.divider)}`,
  bgcolor: 'background.paper',
  color: selected ? 'text.primary' : 'text.secondary',
  fontSize: FONT_SIZES.bodySm,
  fontWeight: 550,
  cursor: 'pointer',
  transition: 'border-color 0.16s ease, color 0.16s ease, background-color 0.16s ease',
  '&:hover': {
    borderColor: theme.palette.text.primary,
    color: 'text.primary',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.functional?.main || theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

export const ratePresetPillSx = (theme, selected = false) => ({
  ...pillSx(theme, selected),
  flex: 1,
  justifyContent: 'center',
  minWidth: 0,
  minHeight: 40,
  px: 2,
});

export const messageFieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: 'background.paper',
    fontSize: FONT_SIZES.body,
    lineHeight: 1.55,
    '& fieldset': {
      borderColor: theme.palette.border?.subtle || theme.palette.divider,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.text.secondary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.functional?.main || theme.palette.primary.main,
      borderWidth: 1,
    },
  },
});

export const footerSx = (theme) => ({
  px: MODAL_PX,
  py: FOOTER_PY,
  borderTop: `1px solid ${theme.palette.border?.subtle || theme.palette.divider}`,
  bgcolor: 'background.paper',
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  justifyContent: 'flex-end',
  gap: { xs: 1, sm: 1.25 },
  flexShrink: 0,
  pb: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', sm: FOOTER_PY },
});

export const secondaryButtonSx = {
  minHeight: 44,
  borderRadius: '10px',
  px: 2,
  color: 'text.secondary',
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    bgcolor: 'action.hover',
    color: 'text.primary',
  },
};

export const primaryButtonSx = (theme) => ({
  minHeight: 44,
  borderRadius: '10px',
  px: 2.5,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  textTransform: 'none',
  fontWeight: 650,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: theme.palette.primary.hover || theme.palette.primary.dark,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
});

export const destructiveButtonSx = (theme) => ({
  ...primaryButtonSx(theme),
  bgcolor: 'error.main',
  color: 'error.contrastText',
  '&:hover': {
    bgcolor: theme.palette.error.dark,
    boxShadow: 'none',
  },
});
